import {useCallback, useEffect, useState} from 'react'
import {useSession} from '~/auth/AuthProvider'
import {getAllPoFTopics, getSoftwareIndicatorConfigurationData, PoFTopicNode, PolygonData, SoftwareIndicatorAttribute, SoftwareIndicatorAttributeLevel, SoftwareIndicatorDimension, updateMinimalPolygon, updateSoftwareIndicatorAttributes} from './apiSoftwareIndicator'
import useSnackbar from '~/components/snackbar/useSnackbar'

export function useSoftwareIndicatorConfiguration(year: string) {
  const {token, user} = useSession()
  const {showErrorMessage} = useSnackbar()
  const [loading, setLoading] = useState(true)

  const [evaluationPeriod, setEvaluationPeriod] = useState<string>(year)
  const [indicatorConfiguration, setIndicatorConfiguration] = useState<SoftwareIndicatorDimension[]>([])
  const [minimalPolygon, setMinimalPolygon] = useState<PolygonData[]>([])
  const [pofTopics, setPofTopics] = useState<PoFTopicNode[]>([])

  useEffect(() => {
    setEvaluationPeriod(year)
    fetchIndicatorConfiguration()
    getPofTopics()
  }, [year])

  const fetchIndicatorConfiguration = useCallback(async () => {
    setLoading(true)
    if (year) {
      const configuration = await getSoftwareIndicatorConfigurationData({year, token})
      setIndicatorConfiguration(configuration)
    }
    setLoading(false)
  }, [token, year])

  useEffect(() => {
    mapIndicatorConfigurationToMinimalScores()
  }, [indicatorConfiguration])

  const mapIndicatorConfigurationToMinimalScores = () => {
    if (indicatorConfiguration) {
      const minimalData: PolygonData[] = indicatorConfiguration.map(dimension => {return {'id': dimension.id, 'name': dimension.name, 'minimum_required_level': dimension.minimum_required_level}})
      setMinimalPolygon(minimalData)
    }
  }

  const updateMinimalScores = useCallback(async (scores: {id: SoftwareIndicatorDimension['id'], minimum_required_level: number}[]) => {

    const resp = await updateMinimalPolygon({polygon: scores, token})

    if (resp.status === 204) {
      await fetchIndicatorConfiguration()
      return resp.message as string
    } else {
      showErrorMessage(`${resp.status} - Failed to update minimal polygon.`)
      return undefined
    }
  }, [token, showErrorMessage, year])

  const getIndicatorDimensions = useCallback(() => indicatorConfiguration ?? [], [indicatorConfiguration])

  const getIndicatorAttributes = useCallback(
    (dimensionId?: SoftwareIndicatorDimension['id']): SoftwareIndicatorAttribute[] => {
      if (!indicatorConfiguration) return []
      if (dimensionId) {
        return indicatorConfiguration.find(d => d.id === dimensionId)?.attributes ?? []
      }
      return indicatorConfiguration.flatMap(d => d.attributes ?? [])
    }, [indicatorConfiguration]
  )

  type GetIndicatorAttributeLevelsOptions =
    | {dimensionId: SoftwareIndicatorDimension['id'], attributeId?: SoftwareIndicatorAttribute['id']} //get attribute levels for dimension
    | {dimensionId?: SoftwareIndicatorDimension['id'], attributeId: SoftwareIndicatorAttribute['id']} //get attribute levels fro attribute
    | {} //get all attribute levels

  const getIndicatorAttributeLevels = useCallback(
    (options: GetIndicatorAttributeLevelsOptions = {}): SoftwareIndicatorAttributeLevel[] => {
      if (!indicatorConfiguration) return []
      //for attribute
      if (typeof options === 'object' && 'attributeId' in options && options.attributeId) {
        for (const d of indicatorConfiguration) {
          const a = d.attributes?.find(a => a.id === options.attributeId)
          if (a?.levels) return a.levels
        }
        return []
      }

      //for dimension
      if (typeof options === 'object' && 'dimensionId' in options && options.dimensionId) {
        const d = indicatorConfiguration.find(d => d.id === options.dimensionId)
        if (!d?.attributes) return []
        return d.attributes.flatMap(a => a.levels ?? [])
      }

      //all attribute levels
      return indicatorConfiguration.flatMap(d => (d.attributes ?? []).flatMap(a => a.levels ?? []))
    }, [indicatorConfiguration]
  )

  const getIndicatorDimensionById = useCallback((dimensionId: SoftwareIndicatorDimension['id']): SoftwareIndicatorDimension | undefined => indicatorConfiguration.find(d => d.id === dimensionId), [indicatorConfiguration])
  const getIndicatorAttributeById = useCallback(
    (dimensionId: SoftwareIndicatorDimension['id'], attributeId: SoftwareIndicatorAttribute['id']) => {
      const d = indicatorConfiguration?.find(d => d.id === dimensionId)
      return d?.attributes?.find(a => a.id === attributeId)
    }, [indicatorConfiguration]
  )


  const updateAttributes = useCallback(async (attributes: {id: SoftwareIndicatorAttribute['id'], name: SoftwareIndicatorAttribute['name'], weight: SoftwareIndicatorAttribute['weight']}[]) => {
    const resp = await updateSoftwareIndicatorAttributes({token, attributes})

    if (resp.status === 204) {
      return resp.message as string
    } else {
      showErrorMessage(`${resp.status} - Failed to update attributes.`)
      return undefined
    }
  }, [token])

  const getPofTopics = useCallback(async () => {
    setLoading(true)
    if (token) {
      const topics: PoFTopicNode | null = await getAllPoFTopics({token})
      setPofTopics(topics?.children ?? [])
    }
    setLoading(false)
  }, [token])

  const indicatorConfigurationMaximumLevel = useCallback((): number | null => {
    if (!indicatorConfiguration) return null
    const attributeLevels: SoftwareIndicatorAttributeLevel[] = getIndicatorAttributeLevels()
    const levelNumbers: number[] = attributeLevels.map(levelObj => levelObj.level)
    return Math.max(...levelNumbers)
  }, [indicatorConfiguration])

  return {
    loading,
    indicatorConfiguration,
    getIndicatorDimensions,
    getIndicatorDimensionById,
    getIndicatorAttributes,
    getIndicatorAttributeById,
    getIndicatorAttributeLevels,
    minimalPolygon,
    updateMinimalScores,
    updateAttributes,
    pofTopics,
    indicatorConfigurationMaximumLevel,
    evaluationPeriod
  }
}
